import {zodResolver} from '@hookform/resolvers/zod'
import {useEffect, useRef, useState} from 'react'
import {useForm} from 'react-hook-form'
import {useGetConfig} from 'src/common/hooks/useGetConfig'
import {Icon} from 'src/common/Icon'
import {LeadFormFields} from 'src/common/LeadFormFields'
import {ROLES} from 'src/Event/components/RegisterForm/constants/formOptions'
import {useHubspotForm} from 'src/Event/hooks/useHubspotForm'
import {z} from 'zod'
import type {PolicySection} from '../hooks/usePolicyGenerator'
import {btn, CATEGORY_OPTIONS, COUNTRY_PRESETS} from './constants'

// ─── HubSpot config ──────────────────────────────────────────────────────────

const HUBSPOT_FORM_ID = '9114e3bb-b394-426c-8d4c-b74511183888'
const SALESFORCE_CAMPAIGN_ID = '701Oj00000oSszyIAC'
// ─── Role options ─────────────────────────────────────────────────────────────

// ─── Form schema ──────────────────────────────────────────────────────────────

const schema = z.object({
  firstname: z.string().min(1, 'Please fill in this field'),
  lastname: z.string().min(1, 'Please fill in this field'),
  email: z.string().min(1, 'Please fill in this field').email('Enter a valid email'),
  company: z.string().min(1, 'Please fill in this field'),
  hs_persona: z.string().min(1, 'Please fill in this field'),
})

type FormValues = z.infer<typeof schema>

// ─── Helpers ──────────────────────────────────────────────────────────────────

const allCategories = CATEGORY_OPTIONS.flatMap((g) => g.categories)
const getCategoryLabel = (value: string) => allCategories.find((c) => c.value === value)?.label ?? value

const buildPolicyText = (sections: PolicySection[], companyName: string): string => {
  const header = `# ${companyName || 'Company'} Expense Policy\n\n`

  const body = sections
    .map((s) => {
      const label = getCategoryLabel(s.id)

      const raw = s.policyText.trimStart().startsWith('<')
        ? s.policyText
            .replace(/<[^>]+>/g, ' ')
            .replace(/\s{2,}/g, ' ')
            .trim()
        : s.policyText

      const text = raw.replace(/^#+\s+.+\n+/, '')
      return `## ${label}\n\n${text}`
    })
    .join('\n\n')

  return header + body
}

// ─── Component ────────────────────────────────────────────────────────────────

export function DownloadDialog({
  pageUid,
  companyName,
  formData,
  sections,
  requestGroupId,
  onClose,
  onLocalPdf,
  pdfGenerating = false,
  onDialogViewed,
  onDialogDismissed,
  onSubmitClicked,
  onEmailSubmitted,
  onEmailRejected,
}: {
  pageUid: string
  companyName: string
  formData: {industry: string; companySize: string; country: string}
  sections: PolicySection[]
  requestGroupId: string
  onClose: () => void
  onLocalPdf?: () => Promise<void>
  pdfGenerating?: boolean
  onDialogViewed?: () => void
  onDialogDismissed?: () => void
  onSubmitClicked?: () => void
  onEmailSubmitted?: (email: string) => void
  onEmailRejected?: (reason: string) => void
}) {
  // Stable ref so the mount effect doesn't need onDialogViewed as a dependency
  const onDialogViewedRef = useRef(onDialogViewed)
  onDialogViewedRef.current = onDialogViewed

  // Prevent background scroll while the dialog is open; fire viewed tracking once on mount
  useEffect(() => {
    document.body.style.overflow = 'hidden'
    onDialogViewedRef.current?.()

    return () => {
      document.body.style.overflow = ''
    }
  }, [])

  const config = useGetConfig()

  const {submit: submitHubspot} = useHubspotForm({
    formId: HUBSPOT_FORM_ID,
    pageUid,
    trackingLocation: pageUid,
    trackDataType: 'expense-policy-download',
    salesforce_campaign_id: SALESFORCE_CAMPAIGN_ID,
  })

  const [serverError, setServerError] = useState<string | null>(null)

  const {
    control,
    handleSubmit,
    setError,
    formState: {isSubmitting},
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {firstname: '', lastname: '', email: '', company: companyName, hs_persona: ''},
    mode: 'onTouched',
  })

  const submitToHubspot = (values: FormValues) => {
    const countryLabel = COUNTRY_PRESETS[formData.country?.toLowerCase()]?.label ?? formData.country
    const industryCap = formData.industry ? formData.industry.charAt(0).toUpperCase() + formData.industry.slice(1) : ''
    const employeeNum = formData.companySize.match(/\d+/)?.[0] ?? formData.companySize

    return submitHubspot({
      firstname: values.firstname,
      lastname: values.lastname,
      email: values.email,
      jobtitle: values.hs_persona,
      company: values.company,
      branche_industry: industryCap,
      numberofemployees: employeeNum,
      billing_country: countryLabel,
    })
  }

  // Validates email via the PDF API — discards the PDF response, only checks status
  const validateEmail = async (email: string) => {
    const markdownPolicyText = buildPolicyText(sections, companyName)
    const pdfUrl = `${config.API_URL}/v1/spend-policy-generator/pdf`

    const res = await fetch(pdfUrl, {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({
        request: {markdownPolicyText, requestGroupId, companyName},
        email,
      }),
    })

    if (!res.ok) {
      const data = await res.json().catch(() => null)
      const isCorporateEmailError = data?.errors?.some((e: {codes?: string[]}) => e.codes?.includes('CorporateEmail'))

      if (isCorporateEmailError) {
        throw Object.assign(new Error('corporate-email'), {type: 'corporate-email'})
      }

      throw new Error(`Validation failed: ${res.status}`)
    }
  }

  const onSubmit = async (values: FormValues) => {
    setServerError(null)
    onSubmitClicked?.()

    try {
      await validateEmail(values.email)
      onEmailSubmitted?.(values.email)
      submitToHubspot(values).catch(console.warn)
      await onLocalPdf?.()
      onClose()
    } catch (err) {
      if (err instanceof Error && err.message === 'corporate-email') {
        onEmailRejected?.('corporate_email')
        setError('email', {message: 'Please use a corporate email address'})
      } else {
        setServerError('Something went wrong. Please try again.')
        console.error(err)
      }
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-moss-black/[70%] p-4 backdrop-blur-[10px]"
      style={{animation: 'epgRevealModal 200ms cubic-bezier(0.25,0,0,1) both'}}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="relative mx-auto grid w-full max-w-[820px] rounded-2xl bg-moss-white shadow-2xl md:grid-cols-2"
        style={{animation: 'epgRevealModal 280ms cubic-bezier(0.25,0,0,1) both'}}
      >
        <button
          type="button"
          onClick={() => {
            onDialogDismissed?.()
            onClose()
          }}
          className="absolute right-3 top-3 z-10 flex size-8 items-center justify-center rounded-full text-black/35 transition-colors hover:bg-black/[0.06] hover:text-moss-black"
          aria-label="Close"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>

        <div className="flex flex-col gap-5 overflow-visible rounded-l-2xl p-6 md:p-8">
          <div>
            <h2 className="text-h5 text-moss-black">Get your {companyName || 'company'} expense policy</h2>
            <p className="text-body-s mt-1 text-black/55">
              Fill in your details and we'll generate a PDF ready to share with your team.
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" data-hs-do-not-collect>
            <LeadFormFields control={control} roleOptions={ROLES} />

            {serverError && (
              <p className="rounded-lg bg-ui-red-150 px-3 py-2 text-[13px] text-ui-red-700">{serverError}</p>
            )}

            <button type="submit" className={`${btn} w-full justify-center`} disabled={isSubmitting || pdfGenerating}>
              {isSubmitting || pdfGenerating ? (
                'Generating…'
              ) : (
                <>
                  <Icon name="download" className="size-4" /> Download PDF
                </>
              )}
            </button>

            <p className="text-body-xs text-center text-black/40">
              You accept our{' '}
              <a
                href="https://www.getmoss.com/legal"
                target="_blank"
                rel="noreferrer"
                className="underline hover:text-moss-black"
              >
                privacy policy
              </a>{' '}
              and{' '}
              <a
                href="https://getmoss.com/public/terms-and-conditions/20240701_INT-en_User_Marketing_Consent_v2.0.pdf"
                target="_blank"
                rel="noreferrer"
                className="underline hover:text-moss-black"
              >
                consent
              </a>{' '}
              to Moss updates. You can opt out anytime.
            </p>
          </form>
        </div>

        <div
          className="hidden flex-col justify-end overflow-hidden rounded-r-2xl p-10 md:flex"
          style={{background: 'linear-gradient(160deg, #0a2218 0%, #0c2e1c 100%)'}}
        >
          <p
            className="mb-4 text-[10px] font-semibold uppercase tracking-[0.12em]"
            style={{color: 'rgba(180,255,68,0.7)'}}
          >
            Expense Policy · {new Date().getFullYear()}
          </p>
          <h2
            className="m-0 font-light leading-[1.05] tracking-[-0.02em]"
            style={{fontSize: 52, color: 'rgba(255,255,255,0.88)'}}
          >
            {companyName || 'Your company'}
          </h2>
          <p className="mt-4 text-[14px] leading-[1.6]" style={{color: 'rgba(255,255,255,0.45)'}}>
            A tailored spend policy covering approval thresholds, categories, limits, and submission requirements.
          </p>
        </div>
      </div>
    </div>
  )
}
